/**
 * Single Prompt Test UI
 */

import { APIClient, ChatMessage } from './api';

export class SinglePromptUI {
  private container: HTMLElement;
  private client: APIClient;

  constructor(container: HTMLElement, client: APIClient) {
    this.container = container;
    this.client = client;
  }

  render(): void {
    this.container.innerHTML = `
      <div class="test-section">
        <h2>Single Prompt Test</h2>
        <p class="description">Test non-streaming single prompt with complete response</p>

        <div class="prompt-container">
          <label for="single-prompt-input">Enter your prompt:</label>
          <textarea id="single-prompt-input" placeholder="What is the capital of Japan?" rows="4"></textarea>

          <div class="controls">
            <div class="control-group">
              <label>Model:</label>
              <select id="single-model">
                <option value="consenger/conversation">consenger/conversation</option>
                <option value="consenger/definition">consenger/definition</option>
                <option value="consenger/translation">consenger/translation</option>
                <option value="consenger/summary">consenger/summary</option>
                <option value="consenger/grammar">consenger/grammar</option>
                <option value="consenger/context">consenger/context</option>
              </select>
            </div>
            <div class="control-group">
              <label>Max Tokens:</label>
              <input type="number" id="single-max-tokens" value="2048" min="100" max="4096" step="100">
            </div>
            <div class="control-group">
              <label>Temperature:</label>
              <input type="number" id="single-temperature" value="0.7" min="0" max="1" step="0.1">
            </div>
            <div class="control-group">
              <label>System Prompt:</label>
              <textarea id="single-system-prompt" placeholder="Optional system message" rows="2"></textarea>
            </div>
          </div>

          <button id="single-send" class="btn btn-primary">Send Prompt</button>
        </div>

        <div id="single-response" class="response-container" style="display: none;"></div>
      </div>
    `;

    this.setupEventListeners();
  }

  destroy(): void {
    this.container.innerHTML = '';
  }

  private setupEventListeners() {
    const sendButton = document.getElementById('single-send')!;
    sendButton.addEventListener('click', () => this.sendPrompt());
  }

  private async sendPrompt() {
    const promptInput = document.getElementById('single-prompt-input') as HTMLTextAreaElement;
    const systemPromptInput = document.getElementById('single-system-prompt') as HTMLTextAreaElement;
    const responseContainer = document.getElementById('single-response')!;
    const sendButton = document.getElementById('single-send') as HTMLButtonElement;

    const prompt = promptInput.value.trim();
    if (!prompt) {
      alert('Please enter a prompt');
      return;
    }

    const messages: ChatMessage[] = [];

    const systemPrompt = systemPromptInput.value.trim();
    if (systemPrompt) {
      messages.push({ role: 'system', content: systemPrompt });
    }

    messages.push({ role: 'user', content: prompt });

    const model = (document.getElementById('single-model') as HTMLSelectElement).value;
    const maxTokens = parseInt(
      (document.getElementById('single-max-tokens') as HTMLInputElement).value
    );
    const temperature = parseFloat(
      (document.getElementById('single-temperature') as HTMLInputElement).value
    );

    // Show loading state
    sendButton.disabled = true;
    sendButton.textContent = 'Sending...';
    responseContainer.style.display = 'block';
    responseContainer.innerHTML = '<div class="loading">Waiting for response...</div>';

    try {
      const startTime = Date.now();
      const response = await this.client.singlePrompt(messages, maxTokens, temperature, model);
      const duration = Date.now() - startTime;

      const content = response.choices[0]?.message?.content || '(no response)';
      const usage = response.usage;

      responseContainer.innerHTML = `
        <div class="response-success">
          <h4>Response</h4>
          <div class="response-content">${this.escapeHtml(content)}</div>

          <div class="response-meta">
            <div class="meta-item">
              <strong>Duration:</strong> ${duration}ms
            </div>
            <div class="meta-item">
              <strong>Model:</strong> ${response.model}
            </div>
            ${
              usage
                ? `
              <div class="meta-item">
                <strong>Tokens:</strong> ${usage.prompt_tokens} prompt + ${usage.completion_tokens} completion = ${usage.total_tokens} total
              </div>
            `
                : ''
            }
          </div>
        </div>
      `;
    } catch (error) {
      responseContainer.innerHTML = `
        <div class="response-error">
          <h4>Error</h4>
          <div class="error-message">${
            error instanceof Error ? this.escapeHtml(error.message) : 'Unknown error'
          }</div>
        </div>
      `;
    } finally {
      sendButton.disabled = false;
      sendButton.textContent = 'Send Prompt';
    }
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}
